import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { createGetTableActions, getAccountingToolbarIcon } from '../shared/tableActions';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { APP_CONFIG, formatDate as formatDateGlobal } from '../../../config/appConfig';
import { mapCodeNameSelectOption } from '../shared/formFieldHelpers';
import type { AccAccount } from './useData';
import { accountCanDelete } from './treeListUtils';

const mapCurrencySelectOption = (item: { id: number; code: string; name: string; isActive?: boolean }) => ({
  value: item.id,
  label: item.name});

export const toAccountPayload = (data: Record<string, unknown>) => {
  const isPosting = !!data.isPosting;
  const rawCurrencyIds = Array.isArray(data.currencyIds) ? data.currencyIds : [];
  const currencyIds = rawCurrencyIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  const rawCompanyIds = Array.isArray(data.companyIds) ? data.companyIds : [];
  const companyIds = rawCompanyIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  const paymentTermsRaw = data.paymentTermsDays;
  const paymentTermsDays =
    paymentTermsRaw === '' || paymentTermsRaw === undefined || paymentTermsRaw === null
      ? undefined
      : Number(paymentTermsRaw);
  const departmentRaw = data.departmentId;
  const departmentId =
    departmentRaw === '' || departmentRaw === undefined || departmentRaw === null
      ? null
      : Number(departmentRaw);
  const accountTypeRaw = data.accountTypeId;
  const accountTypeId =
    accountTypeRaw === '' || accountTypeRaw === undefined || accountTypeRaw === null
      ? null
      : Number(accountTypeRaw);
  const operationalTypeRaw = data.operationalTypeId;
  const operationalTypeId =
    operationalTypeRaw === '' || operationalTypeRaw === undefined || operationalTypeRaw === null
      ? null
      : Number(operationalTypeRaw);

  return {
    parentId: data.parentId ? Number(data.parentId) : undefined,
    code: data.code,
    name: data.name,
    isPosting,
    allowOpenItems: !!data.allowOpenItems,
    isIntercompany: !!data.isIntercompany,
    isBudget: !!data.isBudget,
    ...(paymentTermsDays !== undefined && Number.isFinite(paymentTermsDays)
      ? { paymentTermsDays: Math.max(0, Math.trunc(paymentTermsDays)) }
      : { paymentTermsDays: null }),
    departmentId: departmentId && Number.isFinite(departmentId) ? departmentId : null,
    accountTypeId: accountTypeId && Number.isFinite(accountTypeId) ? accountTypeId : null,
    operationalTypeId: operationalTypeId && Number.isFinite(operationalTypeId) ? operationalTypeId : null,
    isActive: data.isActive ?? true,
    currencyIds: isPosting ? currencyIds : [],
    companyIds: isPosting ? companyIds : []};
};

export const moduleConfig = {
  module: {
    title: 'Chart of accounts',
    permissionModuleKey: 'acc_accounts',
    route: {
      list: '/accounting/coa',
      new: '/accounting/coa/new',
      detail: (id: number) => `/accounting/coa/${id}`,
      edit: (id: number) => `/accounting/coa/${id}/edit`},
    api: { list: '/acc-accounts', detail: (id: number) => `/acc-accounts/${id}` }},
  pagination: { initialItemsPerPage: APP_CONFIG.table.defaultPageSize, itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions },
  search: { placeholder: 'Search accounts…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: AccAccount[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New account', showTitle: false, variant: 'primary' as const, href: '/accounting/coa/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: AccAccount) => item.id,
  },
  emptyStates: { noData: 'Create an account.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete account',
    message: 'Delete this account?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deleteAccAccount } = await import('../../../services/mutations');
      await deleteAccAccount(id);
    }},
  utils: { formatDate: (d?: string) => (d ? formatDateGlobal(d) : '') },
  detail: {
    subtitle: 'Account',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Account not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Account',
    getTitle: (item: AccAccount) => item.name},
  form: {
    new: { title: 'New account', subtitle: 'Create chart of accounts entry', submitText: 'Create' },
    edit: { title: 'Edit account', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formSizing: 'fill' as const,
    formMaxWidth: 'max-w-3xl',
    create: async (data: Partial<AccAccount>) => {
      const { createAccAccount } = await import('../../../services/mutations');
      return createAccAccount(toAccountPayload(data as Record<string, unknown>));
    },
    update: async (id: number, data: Partial<AccAccount>) => {
      const { updateAccAccount } = await import('../../../services/mutations');
      return updateAccAccount(id, toAccountPayload(data as Record<string, unknown>));
    }},
  fields: [
    {
      name: 'parentId',
      label: 'Parent account',
      type: 'select',
      placeholder: 'None',
      helperText: 'Only header accounts (non-posting) can have sub-accounts.',
      optionsEndpointFromFormData: () => '/acc-accounts',
      valueField: 'id',
      mapApiItemToSelectOption: mapCodeNameSelectOption,
      filterDynamicOptionItem: (item, formData) => {
        if (formData.id && Number(item.id) === Number(formData.id)) return false;
        if (!item.isPosting) return true;
        return !!(formData.parentId && Number(item.id) === Number(formData.parentId));
      },
      options: [{ value: '', label: 'None' }]},
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      validation: { required: true, maxLength: 30 }},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      validation: { required: true, maxLength: 100 }},
    {
      name: 'operationalTypeId',
      label: 'Operational type',
      type: 'select',
      placeholder: 'None — Bank, Cash, AP, AR…',
      apiEndpoint: '/acc-operational-types?page=1&limit=500',
      valueField: 'id',
      mapApiItemToSelectOption: mapCodeNameSelectOption,
      filterApiOptionItem: (item: { isActive?: boolean }) => item.isActive !== false,
      options: [{ value: '', label: 'None' }]},
    {
      name: 'accountTypeId',
      label: 'Account type (FS)',
      type: 'select',
      placeholder: 'None — Asset, Liability, Equity…',
      apiEndpoint: '/acc-account-types?page=1&limit=500',
      valueField: 'id',
      mapApiItemToSelectOption: mapCodeNameSelectOption,
      filterApiOptionItem: (item: { isActive?: boolean }) => item.isActive !== false,
      options: [{ value: '', label: 'None' }]},
    {
      name: 'departmentId',
      label: 'Responsible department',
      type: 'select',
      placeholder: 'None',
      apiEndpoint: '/departments?page=1&limit=500',
      valueField: 'id',
      mapApiItemToSelectOption: mapCodeNameSelectOption,
      filterApiOptionItem: (item: { isActive?: boolean }) => item.isActive !== false,
      options: [{ value: '', label: 'None' }]},
    {
      name: 'isPosting',
      label: 'Ledger account',
      type: 'switch',
      switchLabel: 'Post journal entries to this account',
      description: 'Ledger accounts cannot have sub-accounts. Use header accounts to group children.',
      defaultValue: true,
      onChangeSideEffect: (value, setFieldValue) => {
        if (!value) {
          setFieldValue('currencyIds', []);
          setFieldValue('companyIds', []);
        }
      }},
    {
      name: 'currencyIds',
      label: 'Allowed currencies',
      type: 'multiselect',
      placeholder: 'All currencies',
      apiEndpoint: '/currencies?page=1&limit=500',
      valueField: 'id',
      mapApiItemToSelectOption: mapCurrencySelectOption,
      filterApiOptionItem: (item: { isActive?: boolean }) => item.isActive !== false,
      maxDisplay: 3,
      helperText: 'Leave empty to allow all currencies on journal lines.'},
    {
      name: 'companyIds',
      label: 'Allowed companies',
      type: 'multiselect',
      placeholder: 'All companies',
      optionsEndpointFromFormData: () => '/acc-companies',
      valueField: 'id',
      mapApiItemToSelectOption: mapCodeNameSelectOption,
      filterDynamicOptionItem: (item: { isActive?: boolean }) => item.isActive !== false,
      maxDisplay: 3,
      helperText: 'Leave empty to allow all companies on journal lines.'},
    {
      name: 'allowOpenItems',
      label: 'Open items',
      type: 'switch',
      switchLabel: 'Allow open items',
      description: 'Track unpaid receivables and payables on this account.',
      defaultValue: false},
    {
      name: 'paymentTermsDays',
      label: 'Payment terms (days)',
      type: 'number',
      placeholder: 'e.g. 30',
      helperText: 'Number of days until payment is due. Leave empty if not applicable.',
      validation: { min: 0 }},
    {
      name: 'isIntercompany',
      label: 'Intercompany',
      type: 'switch',
      switchLabel: 'Intercompany account',
      description: 'Used for balances between companies in the work area.',
      defaultValue: false},
    {
      name: 'isBudget',
      label: 'Budget',
      type: 'switch',
      switchLabel: 'Budget account',
      description: 'Include this account in budget planning and reporting.',
      defaultValue: false},
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Active',
      description: 'Inactive accounts are hidden from posting and selection lists.',
      defaultValue: true},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'identification',
        title: 'Identification',
        description: 'Account code, name, and position in the chart of accounts.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['parentId', 'code', 'name', 'operationalTypeId', 'accountTypeId', 'departmentId'],
        fieldSpans: {
          parentId: 'md:col-span-12',
          code: 'md:col-span-4',
          name: 'md:col-span-8',
          operationalTypeId: 'md:col-span-4',
          accountTypeId: 'md:col-span-4',
          departmentId: 'md:col-span-4'},
        collapsible: true,
        defaultCollapsed: true},
      {
        id: 'ledger',
        title: 'Ledger type',
        description: 'Controls whether journal lines can be posted here.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['isPosting'],
        fieldSpans: {
          isPosting: 'md:col-span-12'},
        collapsible: true,
        defaultCollapsed: true},
      {
        id: 'journal-restrictions',
        title: 'Journal restrictions',
        description: 'Optionally limit which companies and currencies can post to this ledger. Empty means all.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['companyIds', 'currencyIds'],
        fieldSpans: {
          companyIds: 'md:col-span-6',
          currencyIds: 'md:col-span-6'},
        visibleWhen: (fd) => !!fd.isPosting,
        collapsible: true,
        defaultCollapsed: true},
      {
        id: 'properties',
        title: 'Properties',
        description: 'Additional behaviour for this account.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['allowOpenItems', 'paymentTermsDays', 'isIntercompany', 'isBudget', 'isActive'],
        fieldSpans: {
          allowOpenItems: 'md:col-span-12',
          paymentTermsDays: 'md:col-span-6',
          isIntercompany: 'md:col-span-12',
          isBudget: 'md:col-span-12',
          isActive: 'md:col-span-12'},
        collapsible: true,
        defaultCollapsed: true},
    ]} as FormLayout};

export const getColumns = (formatDate: (d?: string) => string): Column<AccAccount>[] => [
  { key: 'code', header: 'Code', sortable: true },
  { key: 'combLevel', header: 'Comb level', sortable: true, render: (_v, row) => row.combLevel ?? row.code },
  { key: 'name', header: 'Name', sortable: true },
  {
    key: 'operationalType',
    header: 'Op. type',
    sortable: false,
    render: (_v, row) => {
      const op = row.operationalType ?? row.effectiveOperationalType ?? row.effectiveAccountType;
      if (!op) return '—';
      const inherited = !row.operationalType;
      return `${op.name}${inherited ? '*' : ''}`;
    }},
  {
    key: 'accountType',
    header: 'FS type',
    sortable: false,
    render: (_v, row) => (row.accountType ? row.accountType.name : '—')},
  {
    key: 'department',
    header: 'Department',
    sortable: false,
    render: (_v, row) => (row.department ? row.department.name : '—')},
  {
    key: 'isPosting',
    header: 'Posting',
    render: (v) => <Badge variant={v ? 'success' : 'default'}>{v ? 'Ledger' : 'Header'}</Badge>},
  {
    key: 'allowedCompanies',
    header: 'Companies',
    render: (_v, row) =>
      row.isPosting
        ? (row.allowedCompanies?.length
          ? row.allowedCompanies.map((c) => c.company?.code ?? c.companyId).join(', ')
          : 'All')
        : '—'},
  {
    key: 'allowedCurrencies',
    header: 'Currencies',
    render: (_v, row) =>
      row.isPosting
        ? (row.allowedCurrencies?.length
          ? row.allowedCurrencies.map((c) => c.currency?.code ?? c.currencyId).join(', ')
          : 'All')
        : '—'},
  { key: 'allowOpenItems', header: 'Open items', render: (v) => (v ? 'Yes' : 'No') },
  {
    key: 'paymentTermsDays',
    header: 'Terms',
    render: (v) => (v != null ? `${v}d` : '—')},
  { key: 'isIntercompany', header: 'Interco.', render: (v) => (v ? 'Yes' : 'No') },
  { key: 'isBudget', header: 'Budget', render: (v) => (v ? 'Yes' : '—') },
  { key: 'isActive', header: 'Active', render: (v) => <Badge variant={v ? 'success' : 'danger'}>{v ? 'Yes' : 'No'}</Badge> },
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: AccAccount, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Comb level', value: item.combLevel ?? item.code },
      { label: 'Code (segment)', value: item.code },
      { label: 'Name', value: item.name },
      { label: 'Parent', value: item.parent ? item.parent.name : '-' },
      {
        label: 'Operational type',
        value: item.operationalType
          ? `${item.operationalType.name}`
          : (item.effectiveOperationalType ?? item.effectiveAccountType)
            ? `${(item.effectiveOperationalType ?? item.effectiveAccountType)!.name}*`
            : '-'},
      {
        label: 'Account type (FS)',
        value: item.accountType ? item.accountType.name : '-'},
      {
        label: 'Responsible department',
        value: item.department ? item.department.name : '-'},
      {
        label: 'Posting',
        value: item.isPosting ? 'Yes — ledger (no sub-accounts)' : 'No — header'},
      {
        label: 'Allowed companies',
        value: item.isPosting
          ? (item.allowedCompanies?.length
            ? item.allowedCompanies.map((c) => (c.company ? c.company.name : String(c.companyId))).join(', ')
            : 'All companies')
          : '-'},
      {
        label: 'Allowed currencies',
        value: item.isPosting
          ? (item.allowedCurrencies?.length
            ? item.allowedCurrencies.map((c) => c.currency?.code ?? c.currencyId).join(', ')
            : 'All currencies')
          : '-'},
      { label: 'Open items', value: item.allowOpenItems ? 'Yes' : 'No' },
      {
        label: 'Payment terms',
        value: item.paymentTermsDays != null ? `${item.paymentTermsDays} days` : '-'},
      { label: 'Intercompany', value: item.isIntercompany ? 'Yes' : 'No' },
      { label: 'Budget', value: item.isBudget ? 'Yes' : 'No' },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getTableActions = createGetTableActions<AccAccount>(moduleConfig.module.route, {
  showDelete: (item) => accountCanDelete(item)});

export const getToolbarIcon = getAccountingToolbarIcon;
export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<AccAccount> => ({
  parentId: '' as unknown as number,
  code: '',
  name: '',
  departmentId: '' as unknown as number,
  operationalTypeId: '' as unknown as number,
  accountTypeId: '' as unknown as number,
  isPosting: true,
  currencyIds: [],
  companyIds: [],
  allowOpenItems: false,
  paymentTermsDays: '' as unknown as number,
  isIntercompany: false,
  isBudget: false,
  isActive: true});
export const mapItemToFormData = (item: AccAccount): Partial<AccAccount> => ({
  id: item.id,
  parentId: (item.parentId ? String(item.parentId) : '') as unknown as number,
  code: item.code,
  name: item.name,
  departmentId: (item.departmentId ? String(item.departmentId) : '') as unknown as number,
  operationalTypeId: (item.operationalTypeId ? String(item.operationalTypeId) : '') as unknown as number,
  accountTypeId: (item.accountTypeId ? String(item.accountTypeId) : '') as unknown as number,
  isPosting: item.isPosting,
  currencyIds: item.currencyIds ?? [],
  companyIds: item.companyIds ?? [],
  allowOpenItems: item.allowOpenItems,
  paymentTermsDays: (item.paymentTermsDays != null ? String(item.paymentTermsDays) : '') as unknown as number,
  isIntercompany: item.isIntercompany,
  isBudget: item.isBudget ?? false,
  isActive: item.isActive ?? true});
